// Day/night and weather tinting. Per the art policy, time of day and weather
// are engine-side tints — never re-rendered sprite variants.

import { lerp } from '../core/math';
import type { WeatherCondition, WeatherState } from '../core/types';
import { hourOfDay } from '../sim/time';

export type DayPhase = 'dawn' | 'day' | 'dusk' | 'night';

export interface Tint {
  /** Multiplied into sprite colours. */
  color: number;
  /** 0 = no darkening, 1 = full night. */
  darkness: number;
  /** Coop windows glow after dark. */
  windowGlow: number;
}

const PHASE_TINTS: Record<DayPhase, Tint> = {
  dawn: { color: 0xffd9a8, darkness: 0.25, windowGlow: 0.4 },
  day: { color: 0xffffff, darkness: 0, windowGlow: 0 },
  dusk: { color: 0xffb888, darkness: 0.3, windowGlow: 0.6 },
  night: { color: 0x9fb4e0, darkness: 0.62, windowGlow: 1 },
};

/** Half an hour of dawn and dusk on either side of the real sun times. */
export const TWILIGHT_HOURS = 0.5;

/**
 * Hard ceiling on darkening. Night and a storm stack to more than 1.0, which
 * renders the world invisible — and a player has to be able to see their birds
 * during a hurricane at night, which is exactly when it matters most.
 */
export const MAX_DARKNESS = 0.72;

export function dayPhaseFor(simHour: number, weather: WeatherState): DayPhase {
  const hour = hourOfDay(simHour);
  const { sunriseHour, sunsetHour } = weather;

  if (hour >= sunriseHour - TWILIGHT_HOURS && hour < sunriseHour + TWILIGHT_HOURS) return 'dawn';
  if (hour >= sunsetHour - TWILIGHT_HOURS && hour < sunsetHour + TWILIGHT_HOURS) return 'dusk';
  return hour >= sunriseHour && hour < sunsetHour ? 'day' : 'night';
}

const WEATHER_DARKENING: Record<WeatherCondition, number> = {
  CLEAR: 0,
  CLOUDY: 0.12,
  FOG: 0.2,
  RAIN: 0.25,
  STORM: 0.45,
};

const WEATHER_DESATURATION: Record<WeatherCondition, number> = {
  CLEAR: 0,
  CLOUDY: 0.15,
  FOG: 0.45,
  RAIN: 0.3,
  STORM: 0.4,
};

function desaturate(color: number, amount: number): number {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  const grey = r * 0.299 + g * 0.587 + b * 0.114;
  const mix = (channel: number) => Math.round(lerp(channel, grey, amount));
  return (mix(r) << 16) | (mix(g) << 8) | mix(b);
}

/**
 * Blends the time-of-day tint with the current weather. One value the renderer
 * applies to the whole world container.
 */
export function tintFor(simHour: number, weather: WeatherState): Tint {
  const phase = dayPhaseFor(simHour, weather);
  const base = PHASE_TINTS[phase];
  const weatherDark = WEATHER_DARKENING[weather.condition];

  return {
    color: desaturate(base.color, WEATHER_DESATURATION[weather.condition]),
    // Weather can only make it darker, never brighter than the phase allows —
    // and never past the point where the farm stops being readable.
    darkness: Math.min(MAX_DARKNESS, base.darkness + weatherDark),
    windowGlow: Math.max(base.windowGlow, weatherDark > 0.3 ? 0.5 : 0),
  };
}

/** Applies darkness as an alpha over a black overlay — cheap and reversible. */
export function overlayAlphaFor(tint: Tint): number {
  return tint.darkness;
}
